import React, { useState, useEffect } from "react";
import { getClientBoldSignDocuments, generateBoldSignDocumentFromTemplate, syncBoldSignDocumentStatus } from "@/api/clientTerms.jsx";
import { getBoldSignTemplates } from "@/api/terms.jsx";
import { Button} from "@/components/ui/button.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.tsx";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table.tsx";
import { toast } from "sonner";
import { Plus, FileSignature, RefreshCw } from "lucide-react";

export default function ClientTermsAndAgreements({ venueId, venueName }) {
    const [boldSignTemplates, setBoldSignTemplates] = useState([]);
    const [clientBoldSignDocuments, setClientBoldSignDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generatingDocuments, setGeneratingDocuments] = useState(new Set());
    const [syncingDocuments, setSyncingDocuments] = useState(new Set());

    useEffect(() => {
        loadData();
    }, [venueId]);

    const loadData = async () => {
        setLoading(true);
        try {
            // Fetch all active client templates and existing documents for this venue
            const [allTemplates, clientBoldSignDocsData] = await Promise.all([
                getBoldSignTemplates(),
                getClientBoldSignDocuments(venueId)
            ]);

            // Filter to only show templates active for clients
            const boldSignTemplatesData = allTemplates?.filter(t => t.activeForClient) || [];

            setBoldSignTemplates(boldSignTemplatesData || []);
            setClientBoldSignDocuments(clientBoldSignDocsData || []);

            console.log('Loaded data:', {
                templates: boldSignTemplatesData?.length || 0,
                documents: clientBoldSignDocsData?.length || 0
            });
        } catch (error) {
            console.error('Error loading client terms data:', error);
            toast.error('Failed to load client terms data');
        } finally {
            setLoading(false);
        }
    };

    // Group documents by template ID to show all versions
    const documentsByTemplate = {};
    clientBoldSignDocuments.forEach(doc => {
        if (!documentsByTemplate[doc.boldSignTemplateId]) {
            documentsByTemplate[doc.boldSignTemplateId] = [];
        }
        documentsByTemplate[doc.boldSignTemplateId].push(doc);
    });

    // Sort documents within each template by creation date (newest first)
    Object.keys(documentsByTemplate).forEach(templateId => {
        documentsByTemplate[templateId].sort((a, b) =>
            new Date(b.createdAt) - new Date(a.createdAt)
        );
    });

    const handleGenerateDocument = async (templateId) => {
        setGeneratingDocuments(prev => new Set(prev.add(templateId)));
        try {
            const result = await generateBoldSignDocumentFromTemplate(venueId, templateId);
            if (result?.success) {
                toast.success('Document generated successfully');
                await loadData();
            } else {
                toast.error(result?.error || 'Failed to generate document');
            }
        } catch (error) {
            console.error('Error generating document:', error);
            toast.error('Error generating document');
        } finally {
            setGeneratingDocuments(prev => {
                const newSet = new Set(prev);
                newSet.delete(templateId);
                return newSet;
            });
        }
    };

    const handleSyncDocumentStatus = async (documentId) => {
        setSyncingDocuments(prev => new Set(prev.add(documentId)));
        try {
            const result = await syncBoldSignDocumentStatus(venueId, documentId);
            if (result?.success) {
                toast.success('Document status synced successfully');
                await loadData();
            } else {
                toast.error(result?.error || 'Failed to sync document status');
            }
        } catch (error) {
            console.error('Error syncing document status:', error);
            toast.error('Error syncing document status');
        } finally {
            setSyncingDocuments(prev => {
                const newSet = new Set(prev);
                newSet.delete(documentId);
                return newSet;
            });
        }
    };

    const getStatusBadge = (clientDoc) => {
        if (!clientDoc) {
            return <Badge className="bg-gray-100 text-gray-800">Not Generated</Badge>;
        }

        const waitingFor = clientDoc.waitingFor || [];
        const boldSignStatus = clientDoc.boldSignStatus;

        // If "client" is present in waiting_for
        if (waitingFor.includes('client')) {
            return <Badge className="bg-yellow-100 text-yellow-800">Awaiting Client Signature</Badge>;
        }

        // If "ramsden" is present in waiting_for AND "client" is not present
        if (waitingFor.includes('ramsden') && !waitingFor.includes('client')) {
            return <Badge className="bg-blue-100 text-blue-800">Awaiting Your Signature</Badge>;
        }

        // If waiting_for is empty, use bold_sign_status
        if (waitingFor.length === 0 && boldSignStatus) {
            // Map BoldSign statuses to display values
            const statusColors = {
                'Completed': 'bg-green-100 text-green-800',
                'InProgress': 'bg-yellow-100 text-yellow-800',
                'Draft': 'bg-gray-100 text-gray-800',
                'Declined': 'bg-red-100 text-red-800',
                'Cancelled': 'bg-red-100 text-red-800'
            };

            const colorClass = statusColors[boldSignStatus] || 'bg-gray-100 text-gray-800';
            return <Badge className={colorClass}>{boldSignStatus}</Badge>;
        }

        // Fallback
        return <Badge className="bg-gray-100 text-gray-800">Generated</Badge>;
    };

    if (loading) {
        return (
            <Card>
                <CardContent className="p-6">
                    <p>Loading client terms and agreements...</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            {/* BoldSign Documents Section */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileSignature className="w-5 h-5" />
                        BoldSign Documents
                        <Badge variant="secondary">{boldSignTemplates.length}</Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {boldSignTemplates.length === 0 ? (
                        <p className="text-gray-500 py-4">No BoldSign templates available for this client.</p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Template</TableHead>
                                    <TableHead>Signer</TableHead>
                                    <TableHead>Created</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {boldSignTemplates.map((template) => {
                                    const templateDocs = documentsByTemplate[template.id] || [];
                                    const isGenerating = generatingDocuments.has(template.id);

                                    // Show template row with generate button if no documents exist
                                    if (templateDocs.length === 0) {
                                        return (
                                            <TableRow key={template.id}>
                                                <TableCell className="font-medium">
                                                    {template.templateName}
                                                </TableCell>
                                                <TableCell className="text-gray-500">—</TableCell>
                                                <TableCell className="text-gray-500">—</TableCell>
                                                <TableCell>
                                                    <Badge className="bg-gray-100 text-gray-800">Not Generated</Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleGenerateDocument(template.id)}
                                                        disabled={isGenerating}
                                                    >
                                                        {isGenerating ? (
                                                            <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                                        ) : (
                                                            <Plus className="w-4 h-4 mr-1" />
                                                        )}
                                                        Generate
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }

                                    // Show all document versions for this template
                                    return templateDocs.map((clientDoc, index) => {
                                        const isSyncing = syncingDocuments.has(clientDoc.id);
                                        const isFirstRow = index === 0;

                                        return (
                                            <TableRow key={clientDoc.id}>
                                                {isFirstRow && (
                                                    <TableCell className="font-medium" rowSpan={templateDocs.length}>
                                                        {template.templateName}
                                                        <div className="mt-2">
                                                            <Button
                                                                size="sm"
                                                                onClick={() => handleGenerateDocument(template.id)}
                                                                disabled={isGenerating}
                                                            >
                                                                {isGenerating ? (
                                                                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                                                ) : (
                                                                    <Plus className="w-4 h-4 mr-1" />
                                                                )}
                                                                Generate New
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    <div className="text-sm">
                                                        <p className="font-medium">{clientDoc.signerName || 'Unknown'}</p>
                                                        <p className="text-xs text-gray-500">{clientDoc.signerEmail || ''}</p>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <p className="text-sm text-gray-600">
                                                        {new Date(clientDoc.createdAt).toLocaleDateString()}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {new Date(clientDoc.createdAt).toLocaleTimeString()}
                                                    </p>
                                                </TableCell>
                                                <TableCell>
                                                    {getStatusBadge(clientDoc)}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex gap-2 flex-wrap">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => handleSyncDocumentStatus(clientDoc.id)}
                                                            disabled={isSyncing}
                                                        >
                                                            {isSyncing ? (
                                                                <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                                            ) : (
                                                                <RefreshCw className="w-4 h-4 mr-1" />
                                                            )}
                                                            Sync
                                                        </Button>

                                                        {clientDoc.status !== 'completed' && clientDoc.ramsdenSigningLink && (
                                                            <Button
                                                                size="sm"
                                                                variant="secondary"
                                                                onClick={() => window.open(clientDoc.ramsdenSigningLink, '_blank')}
                                                            >
                                                                <FileSignature className="w-4 h-4 mr-1" />
                                                                Sign
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    });
                                })}
                            </TableBody>
                        </Table>
                    )}
                    {boldSignTemplates.length > 0 && (
                        <p className="text-sm text-gray-500 mt-4 italic">
                            To view and download completed documents, please log in to{' '}
                            <a
                                href="https://boldsign.com/esignature-api/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                            >
                                BoldSign
                            </a>.
                        </p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
